import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api from '../services/api';
import Spinner from '../components/ui/Spinner';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import Input from '../components/ui/Input';
import SignPasswordModal from '../components/SignPasswordModal';
import { useToast } from '../context/ToastContext';

/**
 * MyRoute — declararea traseului domiciliu-serviciu
 * Funcționează atât ca pagină normală cât și în flux de onboarding.
 * În onboarding, adaugă un banner și butonul "Continuă" după salvare.
 */
const MyRoute = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = location.state?.isOnboarding;
  const onboardingMessage = location.state?.message;
  const returnTo = location.state?.returnTo;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);

  const [formData, setFormData] = useState({
    homeAddress: '',
    residenceAddress: '',
    workAddress: '',
    transportModes: [], // multi-select: listă de valori selectate
    transportModeOther: '', // text liber pentru „altele..."
    routeDuration: '',
    routeDetails: '',
  });

  useEffect(() => {
    fetchRoute();
  }, []);

  const fetchRoute = async () => {
    try {
      const res = await api.get('/route/me');
      const emp = res.data;
      if (emp) {
        setFormData({
          homeAddress: emp.homeAddress || '',
          residenceAddress: emp.residenceAddress || '',
          workAddress: emp.workAddress || '',
          // transportMode vine ca string separat prin virgulă (multi-select). Migrăm și valorile
          // vechi single (ex: „personal"). „mixt" a fost scos → îl mapăm la personal+public.
          transportModes: emp.transportMode
            ? emp.transportMode
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .flatMap((m) => (m === 'mixt' ? ['personal', 'public'] : [m]))
            : [],
          transportModeOther: emp.transportModeOther || '',
          routeDuration: emp.routeDuration || '',
          routeDetails: emp.routeDetails || '',
        });
      }
    } catch (e) {
      /* no profile yet */
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Toggle pentru un mijloc de transport (checkbox — se pot selecta mai multe deodată).
  const toggleTransport = (value) => {
    setFormData((prev) => {
      const has = prev.transportModes.includes(value);
      const transportModes = has
        ? prev.transportModes.filter((m) => m !== value)
        : [...prev.transportModes, value];
      // Dacă deselectăm „altele", golim și textul liber.
      const transportModeOther = value === 'altele' && has ? '' : prev.transportModeOther;
      return { ...prev, transportModes, transportModeOther };
    });
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (
      !formData.homeAddress.trim() ||
      !formData.residenceAddress.trim() ||
      !formData.workAddress.trim()
    ) {
      showToast(t('myRoute.addressesRequired'), 'error');
      return;
    }
    // „altele" bifat dar fără text → cerem completarea.
    if (formData.transportModes.includes('altele') && !formData.transportModeOther.trim()) {
      showToast(t('myRoute.transportOtherRequired'), 'error');
      return;
    }
    setShowPwdModal(true);
  };

  const submitDeclare = async (password) => {
    setSaving(true);
    try {
      // Serializăm transportModes (array) în string separat prin virgulă pentru API/DB.
      const { transportModes, ...rest } = formData;
      const payload = {
        ...rest,
        transportMode: transportModes.join(','),
        password,
      };
      await api.put('/route/me', payload);
      setSaved(true);
      setShowPwdModal(false);
      if (returnTo) {
        setTimeout(() => navigate(returnTo), 1500);
      } else if (isOnboarding) {
        // Onboarding flow — NU navigăm automat. Userul vede cardul de succes cu
        // butonul „Continuă la dashboard" și apasă EXPLICIT pe el. La navigare către
        // /dashboard, Layout re-rulează `checkFirstTimeSetup` (flag-ul
        // `continueOnboardingAfterRoute`) și deschide wizard-ul. Asta evită ca
        // wizard-ul să apară înainte ca user-ul să confirme că a terminat configurarea
        // traseului (cerință explicită — auto-navigate era considerat invaziv).
      } else {
        setTimeout(() => navigate('/my-profile'), 1500);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setShowPwdModal(false);
        showToast(data.error || t('myRoute.saveError'), 'error');
        return;
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner label={t('myRoute.loading')} />;

  const transportOptions = [
    {
      value: 'personal',
      label: t('myRoute.transport.personal'),
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 00-.879-2.121L16.5 8.25V6.375c0-.621-.504-1.125-1.125-1.125H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125"
          />
        </svg>
      ),
    },
    {
      value: 'public',
      label: t('myRoute.transport.public'),
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125V11.25a4.5 4.5 0 00-4.5-4.5H6.75a4.5 4.5 0 00-4.5 4.5v6.375c0 .621.504 1.125 1.125 1.125"
          />
        </svg>
      ),
    },
    {
      value: 'pe_jos',
      label: t('myRoute.transport.walk'),
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0"
          />
        </svg>
      ),
    },
    {
      value: 'bicicleta',
      label: t('myRoute.transport.bike'),
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      value: 'altele',
      label: t('myRoute.transport.other'),
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 12h.008v.008H6V12zm6 0h.008v.008H12V12zm6 0h.008v.008H18V12z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {showPwdModal && (
        <SignPasswordModal
          title={t('myRoute.declareTitle') || 'Declară traseul'}
          description={
            t('myRoute.declareDesc') ||
            'Vei declara electronic traseul domiciliu-serviciu cu semnătură electronică avansată (AdES). Această declarație constituie probă legală.'
          }
          onSubmit={submitDeclare}
          onClose={() => setShowPwdModal(false)}
        />
      )}
      {/* Banner onboarding */}
      {isOnboarding && (
        <Alert type="info">
          <div>
            <p className="font-bold text-primary-900 dark:text-primary-200">
              {t('myRoute.onboardingStep')}
            </p>
            <p className="text-sm mt-1">{onboardingMessage || t('myRoute.onboardingDefault')}</p>
          </div>
          {/* Progress onboarding */}
          <div className="mt-3 flex gap-2">
            {[
              t('myRoute.progressPassword'),
              t('myRoute.progressSignature'),
              t('myRoute.progressRoute'),
            ].map((s, i) => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full ${i <= 2 ? 'bg-primary-500' : 'bg-primary-200 dark:bg-primary-700'}`}
              />
            ))}
          </div>
        </Alert>
      )}

      {!isOnboarding && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/my-profile')}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-1 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            {t('myRoute.backToProfile')}
          </button>
        </div>
      )}

      <Card padding={false}>
        <div className="bg-gradient-to-r from-accent-600 to-accent-700 dark:from-slate-950 dark:to-slate-900 px-8 py-6 rounded-t-xl">
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
              />
            </svg>
            <div>
              <h1 className="text-xl font-bold text-white">{t('myRoute.heading')}</h1>
              <p className="text-accent-100 text-sm mt-1">{t('myRoute.subheading')}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          {saved && !isOnboarding && (
            <Alert type="success">
              <p className="font-semibold">{t('myRoute.saveSuccess')}</p>
            </Alert>
          )}

          <Input
            label={t('myRoute.homeAddressLabel')}
            helper={t('myRoute.homeAddressHelper')}
            name="homeAddress"
            value={formData.homeAddress}
            onChange={handleChange}
            placeholder={t('myRoute.homeAddressPlaceholder')}
            required
          />

          <Input
            label={t('myRoute.residenceAddressLabel')}
            helper={t('myRoute.residenceAddressHelper')}
            name="residenceAddress"
            value={formData.residenceAddress}
            onChange={handleChange}
            placeholder={t('myRoute.residenceAddressPlaceholder')}
            required
          />

          <Input
            label={t('myRoute.workAddressLabel')}
            helper={t('myRoute.workAddressHelper')}
            name="workAddress"
            value={formData.workAddress}
            onChange={handleChange}
            placeholder={t('myRoute.workAddressPlaceholder')}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="input-label">{t('myRoute.transportMode')}</label>
              <p className="text-xs text-slate-400 dark:text-slate-400 mb-2 -mt-1">
                {t('myRoute.transportModeHelper')}
              </p>
              <div className="space-y-2">
                {transportOptions.map((opt) => {
                  const checked = formData.transportModes.includes(opt.value);
                  return (
                    <div key={opt.value}>
                      <label
                        className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${checked ? 'border-accent-500 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
                      >
                        <input
                          type="checkbox"
                          name="transportModes"
                          value={opt.value}
                          checked={checked}
                          onChange={() => toggleTransport(opt.value)}
                          className="h-4 w-4 rounded text-accent-600"
                        />
                        <span className="text-slate-400 dark:text-slate-400">{opt.icon}</span>
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {opt.label}
                        </span>
                      </label>
                      {/* Câmp liber pentru „altele..." — apare doar când e bifat. */}
                      {opt.value === 'altele' && checked && (
                        <input
                          type="text"
                          name="transportModeOther"
                          value={formData.transportModeOther}
                          onChange={handleChange}
                          placeholder={t('myRoute.transportOtherPlaceholder')}
                          className="input mt-2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label={t('myRoute.durationLabel')}
                helper={t('myRoute.durationHelper')}
                type="number"
                name="routeDuration"
                value={formData.routeDuration}
                onChange={handleChange}
                placeholder={t('myRoute.durationPlaceholder')}
                min="1"
                max="480"
              />
              <div>
                <label className="input-label">{t('myRoute.routeDetailsLabel')}</label>
                <textarea
                  name="routeDetails"
                  value={formData.routeDetails}
                  onChange={handleChange}
                  rows={5}
                  placeholder={t('myRoute.routeDetailsPlaceholder')}
                  className="input resize-none"
                />
              </div>
            </div>
          </div>

          {saved && isOnboarding ? (
            <Alert type="success">
              <div className="text-center w-full">
                <svg
                  className="w-10 h-10 text-accent-500 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.746 3.746 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.746 3.746 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"
                  />
                </svg>
                <p className="font-bold text-accent-900 dark:text-accent-200 text-lg mb-1">
                  {t('myRoute.setupComplete')}
                </p>
                <p className="text-accent-700 dark:text-accent-300 text-sm mb-4">
                  {t('myRoute.setupCompleteDesc')}
                </p>
                <Button variant="accent" size="lg" onClick={() => navigate('/dashboard')}>
                  {t('myRoute.continueToDashboard')}
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </Button>
              </div>
            </Alert>
          ) : (
            <div className="flex gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
              <Button variant="accent" size="lg" className="flex-1" type="submit" loading={saving}>
                {!saving && (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
                {saving ? t('myRoute.saving') : t('myRoute.saveRoute')}
              </Button>
              {!isOnboarding && (
                <Button variant="secondary" size="lg" onClick={() => navigate('/my-profile')}>
                  {t('myRoute.cancel')}
                </Button>
              )}
            </div>
          )}
        </form>
      </Card>

      {/* Info legală */}
      <div className="bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-xl p-4">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          <Trans i18nKey="myRoute.legalNote">
            The home-to-work route is mandatory under <strong>HG 1425/2006</strong> and will be
            included in the individual SSM training sheet. Data is stored securely and is not shared
            with third parties.
          </Trans>
        </p>
      </div>
    </div>
  );
};

export default MyRoute;
